/**
 * events.start_time / end_time are `timestamp without time zone`, and the
 * firm-wide convention is UTC-in-a-naive-column.
 *
 * That convention is not a guess — it is provable from the data.  All-day
 * Amicus events sit at naive hour 7 in summer (862 rows Apr-Oct, 0 rows
 * Dec-Feb) and hour 8 in winter (326 rows Dec-Feb, 1 row Apr-Oct).  That is
 * local midnight expressed in UTC across a DST boundary, and only genuine UTC
 * storage produces that split.  The display path, the recurrence expander, the
 * Amicus importer and googleCalendarSync (which sends `dateTime` with no
 * `timeZone` field) all assume it.
 *
 * The API binds these values into the column verbatim, and Postgres discards
 * the offset designator when casting to `timestamp without time zone` — so
 * sending an ISO string with a Z stores its UTC wall clock, which is exactly
 * what we want.
 *
 * A `<input type="datetime-local">` has no timezone at all: it shows and emits
 * a naive local-looking string.  Feeding it a UTC value renders the wrong
 * clock, and posting its value back stores local-intent as if it were UTC.
 * Before 2026-09-02 the calendar edit form did both, which is bug #821 — a
 * 7-hour error under PDT, 8 under PST.
 *
 * Use these two helpers at EVERY boundary between the DB value and a
 * datetime-local input.  They are deliberately in one shared module: the
 * defect existed in Calendar.js and MatterDetail.js independently, and a
 * per-page copy is how that happens again.
 *
 * We convert against the FIRM's timezone, never the browser's, so an attorney
 * working from Phoenix does not shift the firm's calendar.
 */

export const DEFAULT_FIRM_TZ = 'America/Los_Angeles'

// Building an Intl.DateTimeFormat costs ~100 µs; formatting with one is a few
// µs.  The calendar converts every event on every render (getEventsForDay,
// the due chips' sort — about 1,400 conversions for a 150-event month), so
// each helper reuses one formatter per (purpose, zone).  An invalid zone
// still throws from the constructor exactly as before and is never cached
// (verifier 2026-10-18).
const DTF_CACHE = new Map()
function cachedDtf (purpose, locale, options, tz) {
  const key = `${purpose}|${tz}`
  let dtf = DTF_CACHE.get(key)
  if (!dtf) {
    dtf = new Intl.DateTimeFormat(locale, { ...options, timeZone: tz })
    DTF_CACHE.set(key, dtf)
  }
  return dtf
}

/** The firm timezone, cached on window by whichever page fetched settings first. */
export function firmTz () {
  try {
    return window.__lextrackFirmTz || DEFAULT_FIRM_TZ
  } catch {
    return DEFAULT_FIRM_TZ
  }
}

/**
 * The firm-local calendar date of an instant, as 'YYYY-MM-DD'.
 *
 * For DATE columns written from an instant — e.g. a timer's entry date, "the
 * day the timer started" (docs/specs/timers-one-click-spec.md).  Never
 * `new Date().toISOString().slice(0, 10)` (UTC: a day ahead every Pacific
 * evening) nor date-fns `format(new Date(), 'yyyy-MM-dd')` (the BROWSER's
 * zone: a day ahead for a staff member working from Taipei).
 *
 * `at` is epoch ms or a Date; returns '' for an invalid instant.  An unknown
 * zone falls back to the firm default rather than throwing.
 */
export function firmDateIso (at = Date.now(), tz = firmTz()) {
  const d = at instanceof Date ? at : new Date(Number(at))
  if (Number.isNaN(d.getTime())) return ''
  const fmt = (zone) => {
    const p = {}
    const dtf = cachedDtf('date', 'en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }, zone)
    for (const part of dtf.formatToParts(d)) {
      if (part.type !== 'literal') p[part.type] = part.value
    }
    return `${p.year}-${p.month}-${p.day}`
  }
  try {
    return fmt(tz || DEFAULT_FIRM_TZ)
  } catch {
    return fmt(DEFAULT_FIRM_TZ)
  }
}

/** Offset of `tz` at a given instant, in minutes east of UTC. */
function tzOffsetMinutes (date, tz) {
  const dtf = cachedDtf('offset', 'en-US', {
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }, tz)
  const p = {}
  for (const part of dtf.formatToParts(date)) {
    if (part.type !== 'literal') p[part.type] = part.value
  }
  // Some engines render midnight as hour 24 under hour12:false.
  let hour = Number(p.hour)
  if (hour === 24) hour = 0
  const asIfUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    hour, Number(p.minute), Number(p.second)
  )
  return (asIfUtc - date.getTime()) / 60000
}

/**
 * DB value -> the string a datetime-local input should display.
 * "2026-09-11T09:00:00.000Z" (naive UTC from pg) -> "2026-09-11T02:00" in PDT.
 * Returns '' for null/undefined so it can seed a controlled input directly.
 */
export function utcToLocalInput (value, tz = firmTz()) {
  if (!value) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const dtf = cachedDtf('local', 'en-CA', {
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }, tz)
  const p = {}
  for (const part of dtf.formatToParts(d)) {
    if (part.type !== 'literal') p[part.type] = part.value
  }
  const hour = p.hour === '24' ? '00' : p.hour
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`
}

/**
 * What the user typed -> the value to send to the API.
 * "2026-10-02T10:00" meaning 10am firm-local -> "2026-10-02T17:00:00.000Z".
 * Returns null for empty input so callers can omit the field.
 */
export function localInputToUtc (value, tz = firmTz()) {
  if (!value) return null
  const [datePart, timePart = '00:00'] = String(value).split('T')
  const [y, m, d] = datePart.split('-').map(Number)
  const [hh, mm] = timePart.split(':').map(Number)
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null

  // Treat the typed fields as if they were UTC, then subtract the zone's
  // offset.  Re-check once: near a DST boundary the offset at the guess can
  // differ from the offset at the answer.
  const guess = Date.UTC(y, m - 1, d, hh, mm)
  const off1 = tzOffsetMinutes(new Date(guess), tz)
  let ts = guess - off1 * 60000
  const off2 = tzOffsetMinutes(new Date(ts), tz)
  if (off2 !== off1) ts = guess - off2 * 60000

  return new Date(ts).toISOString()
}

/**
 * A Date whose LOCAL fields hold the FIRM's wall clock.
 *
 * The calendar renders with date-fns (`format`, `isSameDay`) and with
 * `getHours()` for grid positioning, all of which read a Date in the BROWSER's
 * timezone.  Passing a real instant therefore renders the viewer's local time —
 * fine in San Diego, wrong everywhere else.  An attorney working from Taiwan
 * (UTC+8) saw a 9am San Diego event as midnight the NEXT DAY, bucketed into the
 * wrong day cell.
 *
 * The firm's calendar is the firm's calendar: an event at 9am in San Diego is
 * at 9am no matter where you open the laptop.  This returns a Date carrying
 * those firm-local fields so every display path reads the firm's clock.
 *
 * Use this for DISPLAY and DAY-BUCKETING only.  It is deliberately NOT a real
 * instant, so never use it for durations, ordering across zones, or anything
 * sent back to the API — use the raw value for those.
 */
export function toFirmWallClock (value, tz = firmTz()) {
  const local = utcToLocalInput(value, tz)
  if (!local) return null
  const [datePart, timePart] = local.split('T')
  const [y, m, d] = datePart.split('-').map(Number)
  const [hh, mm] = timePart.split(':').map(Number)
  return new Date(y, m - 1, d, hh, mm)
}
